/**
 * Canvas guards — defensive wrappers around Canvas2D APIs that throw
 * on non-finite or invalid inputs.
 *
 * Why this exists: the client error reporter surfaced 6+ instances
 * of these errors in real-user sessions:
 *
 *   "createRadialGradient on CanvasRenderingContext2D: The provided
 *    double value is non-finite"
 *
 * Tracking down every callsite that can produce NaN/Infinity inputs
 * across 16+ uses of createRadialGradient + dozens of other Canvas2D
 * calls is a whack-a-mole game. A single prototype-level guard turns
 * EVERY one of those throws into a silent skip (or safe-fallback
 * draw), eliminating the entire error class at once.
 *
 * Trade-off: monkey-patching prototypes is normally a code smell.
 * Here it's justified because:
 *   - The guard only intercepts ARGUMENT VALIDATION; behavior on
 *     valid inputs is unchanged.
 *   - The failure mode (a frame drops a sub-pixel gradient) is
 *     invisible to users; the failure mode WITHOUT the guard is
 *     a thrown exception that kills the entire render frame.
 *   - It runs once, on module import, before any rendering starts.
 */

let installed = false;

export function installCanvasGuards(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const proto = CanvasRenderingContext2D.prototype;

  // ── createRadialGradient ─────────────────────────────────────
  const origCreateRadial = proto.createRadialGradient;
  proto.createRadialGradient = function (x0, y0, r0, x1, y1, r1) {
    if (
      !Number.isFinite(x0) || !Number.isFinite(y0) || !Number.isFinite(r0) ||
      !Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(r1) ||
      r0 < 0 || r1 < 0
    ) {
      // Return a degenerate 1-unit gradient at origin. Callers will
      // immediately fill with it; visually equivalent to skipping
      // the draw, no exception thrown.
      return origCreateRadial.call(this, 0, 0, 0, 0, 0, 1);
    }
    return origCreateRadial.call(this, x0, y0, r0, x1, y1, r1);
  };

  // ── createLinearGradient ─────────────────────────────────────
  const origCreateLinear = proto.createLinearGradient;
  proto.createLinearGradient = function (x0, y0, x1, y1) {
    if (!Number.isFinite(x0) || !Number.isFinite(y0) || !Number.isFinite(x1) || !Number.isFinite(y1)) {
      return origCreateLinear.call(this, 0, 0, 1, 1);
    }
    return origCreateLinear.call(this, x0, y0, x1, y1);
  };

  // ── arc — most common source of "non-finite radius" canvas warns ─
  const origArc = proto.arc;
  proto.arc = function (x, y, radius, startAngle, endAngle, counterclockwise) {
    if (
      !Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(radius) ||
      !Number.isFinite(startAngle) || !Number.isFinite(endAngle) ||
      radius < 0
    ) {
      return; // skip the path operation entirely
    }
    return origArc.call(this, x, y, radius, startAngle, endAngle, counterclockwise);
  };

  // ── fillRect / strokeRect ────────────────────────────────────
  const origFillRect = proto.fillRect;
  proto.fillRect = function (x, y, w, h) {
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(w) || !Number.isFinite(h)) return;
    return origFillRect.call(this, x, y, w, h);
  };
  const origStrokeRect = proto.strokeRect;
  proto.strokeRect = function (x, y, w, h) {
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(w) || !Number.isFinite(h)) return;
    return origStrokeRect.call(this, x, y, w, h);
  };
}
